import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    // Check initial state for dropdowns and disable button if "Please Select" is active
    this.#checkInitialState();
    
    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Checks the initial state of variant picker and disables add-to-cart button if needed.
   */
  #checkInitialState() {
    // If there are no variant options (product has only default variant), enable the button
    const selects = /** @type {HTMLSelectElement[]} */ (Array.from(this.querySelectorAll('select.variant-option__select')));
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    
    // If there are no selects and no fieldsets, product has no variants - enable button
    if (selects.length === 0 && fieldsets.length === 0) {
      this.#enableAddToCartButton();
      return;
    }
    
    // Check if any dropdown has "Please Select" (empty value) selected
    const hasUnselectedDropdown = selects.some(select => !select.value || select.value === '');
    
    // Check if any fieldset has no checked input
    const hasUnselectedFieldset = fieldsets.some(fieldset => {
      const checkedInput = fieldset.querySelector('input:checked');
      return !checkedInput;
    });

    // If there are unselected options, disable the add-to-cart button
    if (hasUnselectedDropdown || hasUnselectedFieldset) {
      this.#disableAddToCartButton();
    } else {
      // All options are selected, enable the button
      this.#enableAddToCartButton();
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;
    
    // Only skip if it's explicitly "Please Select" (empty value) - allow all other selections
    if (event.target instanceof HTMLSelectElement && (!event.target.value || event.target.value === '')) {
      // User selected "Please Select" - disable button and remove variant from URL
      this.#handlePleaseSelect(event.target);
      return;
    }
  
    // Check if the selected option has no option-value-id (shouldn't happen for real options)
    if (!selectedOption.dataset.optionValueId) {
      return; // Don't process options without IDs
    }

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Check if all options are selected (not "Please Select")
    const allSelects = /** @type {HTMLSelectElement[]} */ (Array.from(this.querySelectorAll('select.variant-option__select')));
    const allFieldsets = /** @type {HTMLFieldSetElement[]} */ (Array.from(this.querySelectorAll('fieldset[ref="fieldsets[]"]')));
    
    // For dropdowns: check if any are still empty/"Please Select"
    const hasUnselectedDropdown = allSelects.some(select => !select.value || select.value === '');
    
    // For radio buttons: check if any fieldset has no checked input
    const hasUnselectedFieldset = allFieldsets.some(fieldset => {
      const checkedInput = fieldset.querySelector('input:checked');
      return !checkedInput;
    });

    // If not all options are selected, disable the add to cart button and return
    if (hasUnselectedDropdown || hasUnselectedFieldset) {
      this.#disableAddToCartButton();
      return;
    }

    // All options are selected, proceed with variant update
    this.#enableAddToCartButton();

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Handles when user selects "Please Select" option.
   * @param {HTMLSelectElement} selectElement - The select element that was changed.
   */
  #handlePleaseSelect(selectElement) {
    // Disable add to cart button
    this.#disableAddToCartButton();
    
    // Remove variant from URL if on product page
    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !selectElement.closest('product-card') &&
      !selectElement.closest('quick-add-dialog');
    
    if (isOnProductPage) {
      const url = new URL(window.location.href);
      url.searchParams.delete('variant');
      if (url.href !== window.location.href) {
        requestYieldCallback(() => {
          history.replaceState({}, '', url.toString());
        });
      }
    }
  }

  /**
   * Disables the add to cart button.
   */
  #disableAddToCartButton() {
    const productForm = this.closest('main')?.querySelector('product-form-component');
    if (productForm) {
      const addToCartButtonContainer = /** @type {any} */ (productForm.querySelector('[ref="addToCartButtonContainer"]'));
      if (addToCartButtonContainer && typeof addToCartButtonContainer.disable === 'function') {
        addToCartButtonContainer.disable();
      }
    }
  }

  /**
   * Enables the add to cart button.
   */
  #enableAddToCartButton() {
    const productForm = this.closest('main')?.querySelector('product-form-component');
    if (productForm) {
      const addToCartButtonContainer = /** @type {any} */ (productForm.querySelector('[ref="addToCartButtonContainer"]'));
      if (addToCartButtonContainer && typeof addToCartButtonContainer.enable === 'function') {
        addToCartButtonContainer.enable();
      }
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // Store current selections before morphing
    /** @type {Record<string, string>} */
    const currentSelections = {};
    const selects = /** @type {HTMLSelectElement[]} */ (Array.from(this.querySelectorAll('select.variant-option__select')));
    selects.forEach((select) => {
      if (select.value && select.value !== '') {
        // Only store non-empty selections (user has made a choice)
        currentSelections[select.id] = select.value;
      }
    });

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    // Restore user selections after morphing
    if (Object.keys(currentSelections).length > 0) {
      // Query selects again after morphing (DOM has changed)
      const newSelects = /** @type {HTMLSelectElement[]} */ (Array.from(this.querySelectorAll('select.variant-option__select')));
      newSelects.forEach((select) => {
        const storedValue = currentSelections[select.id];
        if (storedValue) {
          // Check if the stored value still exists in the new options
          const optionExists = Array.from(select.options).some(
            (option) => option.value === storedValue && option.value !== ''
          );
          if (optionExists) {
            select.value = storedValue;
            // Ensure the selected attribute is set correctly
            Array.from(select.options).forEach((option) => {
              option.removeAttribute('selected');
            });
            const selectedOption = Array.from(select.options).find((option) => option.value === storedValue);
            if (selectedOption) {
              selectedOption.setAttribute('selected', 'selected');
            }
          }
        }
      });
    }

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');
  
    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }
  
    // Filter out placeholder options
    if (selectedOption instanceof HTMLOptionElement && (!selectedOption.value || selectedOption.value === '')) {
      return undefined;
    }
  
    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    
    // Check if it's a placeholder option (empty value)
    if (selectedOption instanceof HTMLOptionElement && (!selectedOption.value || selectedOption.value === '')) {
      return undefined; // Return undefined for placeholder instead of throwing error
    }
    
    const { optionValueId } = selectedOption.dataset;
  
    if (!optionValueId) {
      return undefined; // Return undefined instead of throwing error
    }
  
    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
  
    return selectedOptions
      .filter((option) => {
        // Filter out placeholder options (those with empty value or no optionValueId)
        if (option instanceof HTMLOptionElement && (!option.value || option.value === '')) {
          return false;
        }
        return option.dataset.optionValueId != null;
      })
      .map((option) => {
        const { optionValueId } = option.dataset;
  
        if (!optionValueId) throw new Error('No option value ID found');
  
        return optionValueId;
      });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
